import {
  Box,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { ColumnDef } from '@tanstack/react-table'
import { Check, ExternalLink, RotateCw, Search } from 'lucide-react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'

import { ImageContainer } from '@/components/shared/displayers/ImageContainer'
import { Table } from '@/components/shared/displayers/Table'
import { useDebounce } from '@/hooks/useDebounce'
import { Asset, Equipment } from '@/services/backend/config/entities'
import { Repository } from '@/services/repository'

type Extended = Asset & {
  Equipment?: Equipment
}

type Props = {
  tipoEquipamento: 'EQUIPAMENTO' | 'BATERIA' | undefined
  disponivel?: boolean
  onSelect: (data: Extended) => void
}

function Form({ tipoEquipamento, disponivel, onSelect }: Props) {
  const { value, setValue, debounceValue: patrimonio } = useDebounce('')
  const { data, isFetching, refetch } = useQuery(
    ['asset-searchModal', { patrimonio, tipoEquipamento }],
    async () => {
      return await Repository.Entity.Asset.Search({
        patrimonio,
        tipoEquipamento,
        disponivel,
        manutencao: false,
        ativo: true,
      })
    },
  )

  const columns: ColumnDef<Extended>[] = [
    {
      header: 'Nome',
      accessorKey: 'name',
      cell: ({ row }) => (
        <HStack opacity={row.original.active ? 1 : 0.5}>
          <ImageContainer
            src={row.original?.Equipment?.imageStr ?? undefined}
            size="xs"
          />
          <Box>
            <Text>
              {row.original.assetNumber}
              <Link
                to={`/app/entity/patrimonios/${row.original.id}/detalhes`}
                target="_blank"
              >
                <Icon as={ExternalLink} color="primary.500" pt="2px" ml={2} />
              </Link>
            </Text>
            <Text fontSize="sm" color="gray.400">
              {row.original.Equipment?.name ?? row.original.name}
            </Text>
          </Box>
        </HStack>
      ),
    },
    {
      header: '',
      accessorKey: 'id',
      enableSorting: false,
      cell: ({ row }) => (
        <IconButton
          colorScheme="primary"
          aria-label="Selecionar"
          icon={<Icon as={Check} />}
          onClick={() => onSelect(row.original)}
        />
      ),
    },
  ]

  return (
    <>
      <InputGroup mb={4}>
        <InputLeftElement>
          <Icon as={Search} />
        </InputLeftElement>

        <Input
          placeholder="Pesquisar"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />

        <InputRightElement>
          {isFetching ? (
            <Spinner />
          ) : (
            <IconButton
              aria-label="Pesquisar"
              icon={<Icon as={RotateCw} />}
              onClick={() => refetch()}
              variant="ghost"
            />
          )}
        </InputRightElement>
      </InputGroup>

      <Table
        data={data ?? []}
        columns={columns}
        initialSorting={[
          {
            id: 'name',
            desc: false,
          },
        ]}
        initialState={{
          pagination: {
            pageSize: 5,
          },
        }}
      />
    </>
  )
}

export function Asset_SearchModal({
  tipoEquipamento,
  disponivel,
  onSelect,
}: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure()

  return (
    <>
      <IconButton
        aria-label="Pesquisar"
        icon={<Icon as={Search} />}
        onClick={onOpen}
        colorScheme="primary"
      />

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Pesquisar</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Form
              tipoEquipamento={tipoEquipamento}
              disponivel={disponivel}
              onSelect={(res) => {
                onSelect(res)
                onClose()
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
